/**
 * Phase 9.6.1 — 🎖️ Featured Artist Celebration
 * ---------------------------------------------
 * Lightweight celebration helpers used when a `follower_featured`
 * notification arrives or when a follower lands on /track/:id
 * directly from the celebration toast.
 *
 *  • fireFeaturedToast(notification, onClick)  — sonner toast that links
 *      into /track/:submission_id and records a click on the backend.
 *  • <FeaturedConfettiBurst />                  — CSS-only confetti burst
 *      that runs once for ~2.5s when mounted.
 *  • <FeaturedNowBanner />                      — pulsing inline banner
 *      tag for tracks featured within the last 24h.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const FALLBACK_COVER = "/rgm-logo.png";

export const fireFeaturedToast = ({ notification, navigate }) => {
  if (!notification) return;
  const title = notification.track_title || "a new track";
  const artist = notification.from_user_name || "An artist";
  const submissionId = notification.submission_id;
  toast(
    `🎖️ ${artist} just got Featured!`,
    {
      description: `"${title}" is live on the Feature Board — drop a 🔥`,
      duration: 7000,
      action: submissionId
        ? {
            label: "Listen now",
            onClick: async () => {
              try { await api.post(`/feature/${submissionId}/track-click`); } catch (_e) { /* ignore */ }
              try { await api.post(`/notifications/${notification.notification_id}/read`); } catch (_e) { /* ignore */ }
              navigate?.(`/track/${submissionId}?from=featured`);
            },
          }
        : undefined,
      className: "bg-gradient-to-r from-amber-500/95 via-orange-500/95 to-pink-500/95 text-black border-0",
      icon: "🎖️",
    }
  );
};

/**
 * CSS-only confetti burst — 24 paper particles fall + spin for ~2.5s.
 * Self-unmounts via parent (caller toggles state).
 */
export const FeaturedConfettiBurst = () => {
  const pieces = Array.from({ length: 24 });
  return (
    <div
      className="pointer-events-none fixed inset-0 z-[200] overflow-hidden"
      data-testid="featured-confetti-burst"
      aria-hidden
    >
      {pieces.map((_, i) => {
        const delay = (i * 47) % 600;
        const left = (i * 4.17) % 100;
        const colors = ["#f43f5e", "#f59e0b", "#ec4899", "#a78bfa", "#22d3ee", "#fde047"];
        const color = colors[i % colors.length];
        const duration = 1800 + ((i * 73) % 900);
        return (
          <span
            key={i}
            className="absolute top-[-10px] block w-2 h-3 rounded-sm"
            style={{
              left: `${left}%`,
              background: color,
              transform: "rotate(0deg)",
              animation: `featured-confetti-fall ${duration}ms ${delay}ms linear forwards`,
              opacity: 0.95,
            }}
          />
        );
      })}
      <style>{`
        @keyframes featured-confetti-fall {
          0%   { transform: translateY(0) rotate(0deg);   opacity: 1; }
          80%  { opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

/**
 * Tags a track card as "FEATURED NOW" if featured_at is within the last 24h.
 * Pulsing gold gradient — eye-catching but not loud.
 */
export const FeaturedNowBanner = ({ featuredAt, className = "" }) => {
  if (!featuredAt) return null;
  const ts = new Date(featuredAt).getTime();
  if (!ts) return null;
  const ageMs = Date.now() - ts;
  if (ageMs < 0 || ageMs > 24 * 60 * 60 * 1000) return null;
  return (
    <span
      data-testid="featured-now-banner"
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-gradient-to-r from-yellow-400 via-amber-500 to-pink-500 text-black shadow-[0_0_18px_rgba(251,191,36,0.55)] animate-pulse ${className}`}
    >
      🎖️ Featured Now
    </span>
  );
};

/**
 * Hook: parses ?from=featured on /track/:id and shows the burst once.
 */
export const useFeaturedArrival = (search) => {
  const [showBurst, setShowBurst] = useState(false);
  useEffect(() => {
    if (!search) return;
    const params = new URLSearchParams(search);
    if (params.get("from") === "featured") {
      setShowBurst(true);
      const t = setTimeout(() => setShowBurst(false), 2700);
      return () => clearTimeout(t);
    }
  }, [search]);
  return showBurst;
};

export default FeaturedConfettiBurst;
